import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import MobileShell from "@/components/mobile/MobileShell";
import { api } from "@/lib/api";
import { fmtDateTimeIT, todayISO } from "@/lib/format";
import {
  CalendarClock,
  CheckCircle2,
  Circle,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Plus,
  Bell,
  CalendarDays,
  AlertCircle,
  Pencil,
} from "lucide-react";
import { ReminderDialog } from "@/components/ReminderDialog";
import TodoDayPanel from "@/components/todos/TodoDayPanel";

const DOW_SHORT = ["L", "M", "M", "G", "V", "S", "D"]; // Lun-Dom

// ===== helpers =====
function parseISODate(s) { return new Date(s + "T00:00:00"); }
function fmtISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfWeekMon(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  // getDay(): 0=Dom, 1=Lun ... 6=Sab → vogliamo Mon-start
  const dow = (x.getDay() + 6) % 7; // 0=Lun ... 6=Dom
  x.setDate(x.getDate() - dow);
  return x;
}
function addDays(d, n) {
  const x = new Date(d); x.setDate(x.getDate() + n); return x;
}
function whenToISODate(when) {
  if (!when) return null;
  // when puo' essere "YYYY-MM-DDTHH:MM..." UTC o local. Prendo solo la data locale.
  try {
    const dt = new Date(when);
    if (Number.isNaN(dt.getTime())) return null;
    return fmtISODate(dt);
  } catch { return null; }
}

// ===== Sezione lista (riusato da prima) =====
function Section({ id, title, items, accent, accentBg, icon: Icon, onToggle, onEdit, emptyText }) {
  if (!items || items.length === 0) {
    if (!emptyText) return null;
    return (
      <section data-testid={`agenda-section-${id}`} className="mb-4">
        <div className="flex items-center gap-2 mb-2 px-1">
          <Icon className="w-4 h-4" style={{ color: accent }} strokeWidth={2} />
          <h3 className="text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: accent }}>{title}</h3>
        </div>
        <div className="bg-white text-center py-5 text-[12px] text-slate-400" style={{ border: "1px solid #E8ECF2", borderRadius: 12 }}>
          {emptyText}
        </div>
      </section>
    );
  }
  return (
    <section data-testid={`agenda-section-${id}`} className="mb-4">
      <div className="flex items-center gap-2 mb-2 px-1">
        <Icon className="w-4 h-4" style={{ color: accent }} strokeWidth={2} />
        <h3 className="text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: accent }}>{title}</h3>
        <span
          className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
          style={{ background: accentBg, color: accent, minWidth: 18, textAlign: "center" }}
        >
          {items.length}
        </span>
      </div>
      <div className="space-y-1.5">
        {items.map((r) => (
          <ItemRow key={r.id} item={r} onToggle={onToggle} onEdit={onEdit} accent={accent} />
        ))}
      </div>
    </section>
  );
}

function ItemRow({ item, onToggle, onEdit, accent }) {
  const isAppt = !!item.appointment;
  return (
    <div
      className="flex items-start gap-3 px-3 py-2.5 active:bg-slate-50 transition-colors"
      style={{
        background: item.completed ? "#FAFBFC" : "#fff",
        border: "1px solid #E8ECF2",
        borderLeft: `3px solid ${isAppt ? "#B7955B" : accent}`,
        borderRadius: 12,
        opacity: item.completed ? 0.6 : 1,
      }}
      data-testid={`agenda-row-${item.id}`}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggle(item); }}
        className="mt-0.5 shrink-0 active:scale-90 transition-transform p-1 -m-1"
        data-testid={`agenda-toggle-${item.id}`}
        aria-label={item.completed ? "Riapri" : "Completa"}
      >
        {item.completed ? <CheckCircle2 className="w-5 h-5" style={{ color: "#16A34A" }} strokeWidth={2} /> : <Circle  style={{ color: "#CBD5E1" }} strokeWidth={1.8} />}
      </button>
      <button type="button" onClick={() => onEdit(item)} className="flex-1 min-w-0 text-left" data-testid={`agenda-edit-${item.id}`}>
        <div className="flex items-center gap-1.5">
          {isAppt ? <CalendarDays className="w-3 h-3 shrink-0" style={{ color: "#B7955B" }} strokeWidth={2.2} /> : <Bell  style={{ color: accent }} strokeWidth={2.2} />}
          <p className={`text-[13px] font-semibold truncate ${item.completed ? "line-through text-slate-400" : "text-slate-800"}`}>
            {item.title || (isAppt ? "Appuntamento" : "Promemoria")}
          </p>
          {isAppt && item.ownerName && !item.isMine && (
            <span
              className="shrink-0 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ml-auto truncate max-w-[80px]"
              style={{ background: "rgba(183,149,91,0.12)", color: "#8a6e3d", border: "1px solid rgba(183,149,91,0.3)" }}
              title={`Appuntamento creato da ${item.ownerName}`}
            >
              {item.ownerName}
            </span>
          )}
        </div>
        {item.when && (
          <p className="text-[11px] tabular-nums mt-0.5 font-medium" style={{ color: "#B7955B" }}>{fmtDateTimeIT(item.when)}</p>
        )}
        {item.notes && <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 leading-tight">{item.notes}</p>}
      </button>
      {item.clientId && (
        <Link to={`/m/clienti/${item.clientId}`} onClick={(e) => e.stopPropagation()} className="shrink-0 self-center p-1 -m-1 active:opacity-60" data-testid={`agenda-goclient-${item.id}`} aria-label="Apri cliente">
          <ChevronRight className="w-4 h-4" style={{ color: "#94A3B8" }} />
        </Link>
      )}
    </div>
  );
}

// ===== Strip settimanale swipeable =====
function WeekStrip({ weekStart, selectedDate, todayDate, countByDate, onSelect, onPrev, onNext }) {
  const startTouchX = useRef(null);
  const startTouchY = useRef(null);

  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = addDays(weekStart, i);
    const iso = fmtISODate(d);
    days.push({
      iso,
      dow: DOW_SHORT[i],
      day: d.getDate(),
      isToday: iso === todayDate,
      isSelected: iso === selectedDate,
      count: countByDate[iso] || 0,
    });
  }

  const handleTouchStart = (e) => {
    startTouchX.current = e.touches[0].clientX;
    startTouchY.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e) => {
    if (startTouchX.current == null) return;
    const dx = e.changedTouches[0].clientX - startTouchX.current;
    const dy = e.changedTouches[0].clientY - startTouchY.current;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) onNext(); else onPrev();
    }
    startTouchX.current = null;
    startTouchY.current = null;
  };

  const monthLabel = weekStart.toLocaleDateString("it-IT", { month: "long", year: "numeric" });

  return (
    <div
      className="mb-3"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      data-testid="mobile-agenda-weekstrip"
    >
      {/* Header con label mese e frecce */}
      <div className="flex items-center justify-between mb-2 px-1">
        <button
          type="button"
          onClick={onPrev}
          data-testid="agenda-week-prev"
          className="p-1 active:scale-90 transition-transform"
          aria-label="Settimana precedente"
        >
          <ChevronLeft className="w-4 h-4 text-slate-500" />
        </button>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-600 capitalize">{monthLabel}</p>
        <button
          type="button"
          onClick={onNext}
          data-testid="agenda-week-next"
          className="p-1 active:scale-90 transition-transform"
          aria-label="Settimana successiva"
        >
          <ChevronRight className="w-4 h-4 text-slate-500" />
        </button>
      </div>
      {/* Grid 7 giorni */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => (
          <button
            key={d.iso}
            type="button"
            onClick={() => onSelect(d.iso)}
            data-testid={`agenda-day-${d.iso}`}
            className="flex flex-col items-center py-2 active:scale-95 transition-transform relative"
            style={{
              background: d.isSelected ? "#0A192F" : "#fff",
              color: d.isSelected ? "#fff" : "#0A192F",
              border: d.isToday && !d.isSelected ? "1.5px solid #B7955B" : "1px solid #E8ECF2",
              borderRadius: 10,
              boxShadow: d.isSelected ? "0 2px 6px rgba(10,25,47,0.18)" : "none",
            }}
          >
            <span
              className="text-[9px] font-bold uppercase tracking-wider leading-none mb-1"
              style={{ color: d.isSelected ? "rgba(255,255,255,0.7)" : "#94A3B8" }}
            >
              {d.dow}
            </span>
            <span
              className="text-[14px] font-semibold leading-none tabular-nums"
              style={{ color: d.isSelected ? "#fff" : d.isToday ? "#B7955B" : "#0A192F" }}
            >
              {d.day}
            </span>
            {d.count > 0 && (
              <span
                className="mt-1 text-[8px] font-bold leading-none px-1 py-0.5 rounded-full tabular-nums"
                style={{
                  background: d.isSelected ? "#B7955B" : d.isToday ? "rgba(183,149,91,0.15)" : "rgba(10,25,47,0.08)",
                  color: d.isSelected ? "#fff" : d.isToday ? "#B7955B" : "#0A192F",
                  minWidth: 14,
                  textAlign: "center",
                }}
              >
                {d.count}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ===== Bucketize per le sezioni di sintesi =====
function bucketize(items, todayStr) {
  const today = parseISODate(todayStr);
  const tomorrow = addDays(today, 1);
  const week = addDays(today, 7);

  const buckets = { overdue: [], today: [], week: [], future: [], completed: [] };
  for (const it of items) {
    if (it.completed) { buckets.completed.push(it); continue; }
    if (!it.when) { buckets.future.push(it); continue; }
    const w = new Date(it.when);
    if (Number.isNaN(w.getTime())) { buckets.future.push(it); continue; }
    if (w < today) { buckets.overdue.push(it); continue; }
    if (w < tomorrow) { buckets.today.push(it); continue; }
    if (w < week) { buckets.week.push(it); continue; }
    buckets.future.push(it);
  }
  buckets.overdue.sort((a, b) => (b.when || "").localeCompare(a.when || ""));
  ["today", "week", "future"].forEach((k) => buckets[k].sort((a, b) => (a.when || "").localeCompare(b.when || "")));
  buckets.completed.sort((a, b) => (b.when || "").localeCompare(a.when || ""));
  return buckets;
}

export default function MobileAgenda() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAppointment, setDialogAppointment] = useState(false);
  const [dialogInitial, setDialogInitial] = useState(null);
  // Filtro ownership: "all" | "mine" | "others"
  const [ownerFilter, setOwnerFilter] = useState(() => {
    try { return localStorage.getItem("ba-agenda-owner") || "all"; } catch { return "all"; }
  });
  useEffect(() => { try { localStorage.setItem("ba-agenda-owner", ownerFilter); } catch {} }, [ownerFilter]);

  const today = todayISO();
  const [selectedDate, setSelectedDate] = useState(today);
  const [weekStart, setWeekStart] = useState(() => startOfWeekMon(parseISODate(today)));

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/reminders", { params: { limit: 500, sort: "when_asc" } });
      setItems(r.data.items || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const toggle = async (item) => {
    try {
      await api.patch(`/reminders/${item.id}`, { completed: !item.completed });
      setItems((prev) => prev.map((r) => (r.id === item.id ? { ...r, completed: !r.completed } : r)));
    } catch {}
  };

  const openNewReminder = () => { setDialogInitial(null); setDialogAppointment(false); setDialogOpen(true); };
  const openNewAppt = () => { setDialogInitial(null); setDialogAppointment(true); setDialogOpen(true); };
  const editItem = (item) => { setDialogInitial(item); setDialogAppointment(!!item.appointment); setDialogOpen(true); };

  // Conteggi per il segmented filtro
  const ownerCounts = useMemo(() => ({
    all: items.length,
    mine: items.filter((r) => r.isMine !== false).length,
    others: items.filter((r) => r.appointment && r.isMine === false).length,
  }), [items]);

  // items filtrati per ownership
  const filteredItems = useMemo(() => {
    if (ownerFilter === "all") return items;
    if (ownerFilter === "mine") return items.filter((r) => r.isMine !== false);
    if (ownerFilter === "others") return items.filter((r) => r.appointment && r.isMine === false);
    return items;
  }, [items, ownerFilter]);

  // === Calcolo derivati ===
  const buckets = useMemo(() => bucketize(filteredItems, today), [filteredItems, today]);

  // count impegni per data ISO (solo non completati)
  const countByDate = useMemo(() => {
    const m = {};
    for (const it of filteredItems) {
      if (it.completed) continue;
      const iso = whenToISODate(it.when);
      if (!iso) continue;
      m[iso] = (m[iso] || 0) + 1;
    }
    return m;
  }, [filteredItems]);

  // items del giorno selezionato (anche completati)
  const dayItems = useMemo(() => {
    const list = filteredItems.filter((it) => whenToISODate(it.when) === selectedDate);
    list.sort((a, b) => (a.when || "").localeCompare(b.when || ""));
    return list;
  }, [filteredItems, selectedDate]);

  const totalActive = buckets.overdue.length + buckets.today.length + buckets.week.length + buckets.future.length;

  const headerAction = (
    <button
      type="button"
      onClick={openNewReminder}
      data-testid="mobile-agenda-new-rem-header"
      className="flex items-center justify-center w-9 h-9 rounded-full active:scale-95 transition-transform"
      style={{ background: "rgba(183,149,91,0.18)", border: "1px solid rgba(183,149,91,0.4)" }}
      aria-label="Nuovo promemoria"
    >
      <Pencil className="w-4 h-4" style={{ color: "#B7955B" }} strokeWidth={2} />
    </button>
  );

  // Helper UI per la card "giorno selezionato"
  const selectedDateObj = parseISODate(selectedDate);
  const isTodaySelected = selectedDate === today;
  const dayLabel = selectedDateObj.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

  return (
    <MobileShell title="Agenda" subtitle={`${totalActive} attivi`} action={headerAction}>
      {/* CTA in cima */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <button
          type="button"
          onClick={openNewReminder}
          data-testid="mobile-agenda-new-rem"
          className="flex items-center justify-center gap-2 py-3 text-[13px] font-semibold text-white active:scale-[0.98] transition-transform"
          style={{ background: "#0A192F", borderRadius: 12, boxShadow: "0 1px 3px rgba(10,25,47,0.18)" }}
        >
          <Pencil className="w-4 h-4" strokeWidth={2} />
          Promemoria
        </button>
        <button
          type="button"
          onClick={openNewAppt}
          data-testid="mobile-agenda-new-appt"
          className="flex items-center justify-center gap-2 py-3 text-[13px] font-semibold text-white active:scale-[0.98] transition-transform"
          style={{ background: "#B7955B", borderRadius: 12, boxShadow: "0 1px 3px rgba(183,149,91,0.25)" }}
        >
          <CalendarDays className="w-4 h-4" strokeWidth={2} />
          Appuntamento
        </button>
      </div>

      {/* To-Do del giorno (globale) */}
      <TodoDayPanel />

      {/* Segmented filtro ownership */}
      <div className="flex items-center gap-1 mb-3 overflow-x-auto" data-testid="mob-agenda-owner-filter">
        {[
          { val: "all", label: "Tutti", count: ownerCounts.all },
          { val: "mine", label: "I miei", count: ownerCounts.mine },
          { val: "others", label: "Del team", count: ownerCounts.others },
        ].map((opt) => (
          <button
            key={opt.val}
            type="button"
            onClick={() => setOwnerFilter(opt.val)}
            data-testid={`mob-agenda-filter-${opt.val}`}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-full transition-all shrink-0"
            style={{
              background: ownerFilter === opt.val ? "#0A192F" : "#fff",
              color: ownerFilter === opt.val ? "#fff" : "#475569",
              border: ownerFilter === opt.val ? "1px solid #0A192F" : "1px solid #E2E8F0",
            }}
          >
            {opt.label}
            <span
              className="text-[9px] tabular-nums px-1 py-0.5 rounded-full font-bold leading-none"
              style={{
                background: ownerFilter === opt.val ? "rgba(183,149,91,0.25)" : "rgba(148,163,184,0.18)",
                color: ownerFilter === opt.val ? "#D4AB6E" : "#475569",
                minWidth: 16,
                textAlign: "center",
              }}
            >
              {opt.count}
            </span>
          </button>
        ))}
      </div>

      {/* Strip settimanale swipeable */}
      <WeekStrip
        weekStart={weekStart}
        selectedDate={selectedDate}
        todayDate={today}
        countByDate={countByDate}
        onSelect={(iso) => {
          setSelectedDate(iso);
          // se il giorno selezionato non e' nella settimana visualizzata, sposta
          const d = parseISODate(iso);
          const ws = startOfWeekMon(d);
          if (ws.getTime() !== weekStart.getTime()) setWeekStart(ws);
        }}
        onPrev={() => setWeekStart((w) => addDays(w, -7))}
        onNext={() => setWeekStart((w) => addDays(w, 7))}
      />
      {/* Quick action: torna a oggi se non e' la settimana corrente */}
      {weekStart.getTime() !== startOfWeekMon(parseISODate(today)).getTime() && (
        <div className="flex justify-center mb-3">
          <button
            type="button"
            onClick={() => {
              setSelectedDate(today);
              setWeekStart(startOfWeekMon(parseISODate(today)));
            }}
            data-testid="agenda-back-today"
            className="text-[11px] font-semibold px-3 py-1 rounded-full active:scale-95 transition-transform"
            style={{ background: "rgba(183,149,91,0.12)", color: "#B7955B", border: "1px solid rgba(183,149,91,0.3)" }}
          >
            ↺ Torna a oggi
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#B7955B" }} />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center py-12 text-center bg-white" style={{ borderRadius: 12, border: "1px solid #E8ECF2" }}>
          <CalendarClock className="w-8 h-8 mb-3" style={{ color: "#E8ECF2" }} strokeWidth={1} />
          <p className="text-[14px] font-medium text-slate-500">Nessun promemoria</p>
          <p className="text-[12px] text-slate-400 mt-1">Tocca "Promemoria" qui sopra per crearne uno</p>
        </div>
      ) : (
        <>
          {/* SCADUTI - solo se ce ne sono (sempre dal real today, indipendente dal giorno selezionato) */}
          {buckets.overdue.length > 0 && (
            <Section
              id="overdue"
              title="Scaduti"
              items={buckets.overdue}
              accent="#DC2626"
              accentBg="rgba(220,38,38,0.10)"
              icon={AlertCircle}
              onToggle={toggle}
              onEdit={editItem}
            />
          )}

          {/* CARD GIORNO SELEZIONATO (oggi o quello tappato sulla strip) */}
          <section data-testid="agenda-section-selected-day" className="mb-4">
            <div
              className="px-4 py-3 mb-2"
              style={{
                background: isTodaySelected
                  ? "linear-gradient(135deg, #0A192F 0%, #0D1E35 100%)"
                  : "linear-gradient(135deg, #1E293B 0%, #334155 100%)",
                borderRadius: 12,
                boxShadow: "0 1px 3px rgba(10,25,47,0.18)",
              }}
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-[9px] font-bold uppercase tracking-[0.18em]" style={{ color: "#B7955B" }}>
                    {isTodaySelected ? "Oggi" : "Giorno selezionato"}
                  </p>
                  <p className="text-white font-serif text-[20px] leading-tight capitalize truncate">{dayLabel}</p>
                </div>
                <div className="text-right shrink-0 ml-2">
                  <p className="text-white font-serif text-[28px] leading-none tabular-nums">{dayItems.length}</p>
                  <p className="text-[9px] uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.65)" }}>impegni</p>
                </div>
              </div>
            </div>
            {dayItems.length > 0 ? (
              <div className="space-y-1.5">
                {dayItems.map((r) => (
                  <ItemRow key={r.id} item={r} onToggle={toggle} onEdit={editItem} accent={isTodaySelected ? "#0A192F" : "#475569"} />
                ))}
              </div>
            ) : (
              <div className="bg-white text-center py-5 text-[12px] text-slate-400" style={{ border: "1px solid #E8ECF2", borderRadius: 12 }}>
                {isTodaySelected ? "Nessun impegno per oggi 🎉" : "Nessun impegno in questo giorno"}
              </div>
            )}
          </section>

          {/* PROSSIMI 7 GIORNI: nascondi se selectedDate non e' oggi (per evitare ridondanza con il giorno scelto) */}
          {isTodaySelected && (
            <Section
              id="week"
              title="Prossimi 7 giorni"
              items={buckets.week}
              accent="#0A192F"
              accentBg="rgba(10,25,47,0.10)"
              icon={CalendarClock}
              onToggle={toggle}
              onEdit={editItem}
            />
          )}

          {/* FUTURI: idem, solo in vista "oggi" */}
          {isTodaySelected && (
            <Section
              id="future"
              title="Futuri"
              items={buckets.future}
              accent="#475569"
              accentBg="rgba(71,85,105,0.10)"
              icon={CalendarDays}
              onToggle={toggle}
              onEdit={editItem}
            />
          )}

          {/* COMPLETATI - sempre in fondo se ce ne sono */}
          {buckets.completed.length > 0 && isTodaySelected && (
            <Section
              id="completed"
              title="Completati di recente"
              items={buckets.completed.slice(0, 20)}
              accent="#16A34A"
              accentBg="rgba(22,163,74,0.10)"
              icon={CheckCircle2}
              onToggle={toggle}
              onEdit={editItem}
            />
          )}
        </>
      )}

      {/* FAB matita per nuovo promemoria - sempre visibile */}
      <button
        type="button"
        onClick={openNewReminder}
        data-testid="mobile-agenda-fab"
        aria-label="Nuovo promemoria"
        className="fixed right-4 z-30 flex items-center justify-center active:scale-90 transition-transform"
        style={{
          bottom: "calc(72px + env(safe-area-inset-bottom, 0))",
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #B7955B 0%, #A48148 100%)",
          color: "#fff",
          boxShadow: "0 6px 20px rgba(183,149,91,0.4), 0 2px 6px rgba(10,25,47,0.15)",
        }}
      >
        <Plus className="w-6 h-6" strokeWidth={2.4} />
      </button>

      <ReminderDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={() => { setDialogOpen(false); reload(); }}
        appointment={dialogAppointment}
        initial={dialogInitial || undefined}
        clientId={dialogInitial?.clientId || null}
        clientName={null}
      />
    </MobileShell>
  );
}
